import { Api } from '../typings/api';
import { DRIVE_ITEM_LIST_PAGE_SIZE, ListDisplayType } from '../containers/DriveItemList/constants';
import { OfflineManagerState, DownloadStatus } from '../containers/OfflineManager/reducer';
import Utils from '.';
import UserAgentUtils from './userAgent';
import UrlUtils from './url';
import DownloadManagerUtils from './downloadManager';
import { DownloadManagerState } from '../containers/DownloadManager/reducer';
import { DriveItemInfo, DriveListState } from '../containers/DriveItemList/reducer';
import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import { ShareOrigin } from '../containers/Share/actions';
import { ReadItemsListState } from '../containers/ReadStatus/reducer';
import store from '../store';
import { AppType } from '../containers/App/reducer';
import LocalStorageManager from './localStorage/manager';
import { RouterParamsState } from '../containers/SetRouteParams/reducer';
import RouteUtils from './routes';

const I18n = require('react-redux-i18n').I18n;
const minimumWidthRow = 360;
const minimumWidth = 720;
const maxOriginalImageSize = 2048;

enum ResolutionDisplayType {
	Card = 'Card',
	RowItem = 'RowItem',
	Detail = 'Detail',
	Theater = 'Theater',
	Coverflow = 'Coverflow'
}

enum ActionButton {
	Read = 'read',
	Download = 'download',
	Update = 'update',
	Delete = 'delete',
	Cancel = 'cancel',
	Required = 'required'
}

enum ActionButtonStyle {
	Bar = 'Bar',
	Icon = 'Icon',
	IconBold = 'IconBold',
	ParametersPage = 'ParametersPage'
}


interface ThumbnailDescriptor {
	isImage: boolean;
	src: string;
}

class DriveItemUtils {

	static getDisplayName(driveItem: Api.DriveItem): string {
		const { titles, label } = driveItem;
		const multiAppLogin = DriveItemUtils.getMultiAppLogin();

		const SHARE_DIR_DEFAULT_LABEL = 'SHARED_DIR';
		if (label === SHARE_DIR_DEFAULT_LABEL) {
			return I18n.t('messages.shared_with_me');
		}

		if (multiAppLogin && label === multiAppLogin) {
			return I18n.t('messages.my_documents');
		}

		const titleKeys = Object.keys(titles);
		return !!titleKeys.length ? titles[titleKeys[0]] : label;
	}

	static getMultiAppLogin(): string | null {
		const { multiAppAuthenticationInfos, appInfos } = store.getState();
		const { credentials } = multiAppAuthenticationInfos;
		const { type } = appInfos;

		if (type === AppType.Multiple) {
			if (credentials && credentials.login) {
				return credentials.login;
			} else {
				// for web version
				return LocalStorageManager.getUsername();
			}
		}

		return null;
	}

	static getLocale(driveItem: Api.DriveItem): string {
		const { titles, descriptions } = driveItem;
		const titleKeys = Object.keys(titles);
		const descriptionKeys = Object.keys(descriptions);
		return titleKeys[0] || descriptionKeys[0] || '';
	}

	static getExtension(driveItem: Api.DriveItem): string {
		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
			case Api.DriveItemType.Gallery:
				return '';
			case Api.DriveItemType.Publication:
				return I18n.t(`messages.extension_flip`);
			case Api.DriveItemType.ExternalLink:
				return I18n.t(`messages.extension_external_link`);
		}

		if (driveItem.originalFile) {
			return Utils.getFileExtension(driveItem.originalFile);
		}
		return '';
	}

	static isPreviewable(driveItem: Api.DriveItem): boolean {
		return [Api.DriveItemType.Image, Api.DriveItemType.Movie, Api.DriveItemType.Sound].includes(driveItem.type);
	}

	static getThumbnailDescriptor = (
		driveItem: Api.DriveItem,
		isBox: boolean,
		offlineManager: OfflineManagerState,
		config: Api.Config,
		resolutions: { [k: string]: Api.ResolutionGroup},
		displayType?: ListDisplayType
	): ThumbnailDescriptor => {
		if (UserAgentUtils.isMobileApp) {
			const driveItemStatus = offlineManager.thumbMap[driveItem.id];
			if (driveItemStatus && driveItemStatus.status === DownloadStatus.DOWNLOADED) {
				const src = Utils.getDeviceThumbnailPath(config, driveItem, resolutions, false);
				return { isImage: true, src };
			}
		} else if (DriveItemUtils.hasIcon(driveItem)) {
			const src = DriveItemUtils.getThumbnail(driveItem, config, resolutions, isBox, displayType);
			return { isImage: true, src };
		}
		return { isImage: false, src: DriveItemUtils.getDefaultThumbnail(driveItem) };
	}

	static getThumbnail(
		driveItem: Api.DriveFolderInfo,
		galleryConfig: Api.Config,
		globalResolutions: { [k: string]: Api.ResolutionGroup },
		isBox: boolean,
		displayType?: ListDisplayType): string {

		// fallback order iconWithResolution > iconOriginalUrl > defaultIcon
		const resolutions = driveItem.resolutions || globalResolutions;
		const { icon } = driveItem;
		if (icon) {
			const iconId = this.getIconId(driveItem);

			if (iconId < 0) {
				return Utils.getUrlForSecuredResource(UrlUtils.addCredentialsToCover(icon.replace('$media$', `${UrlUtils.getRootApiClient(galleryConfig)}/`)));
			}

			const currentDisplayType = displayType && displayType === ListDisplayType.Row ? ResolutionDisplayType.RowItem : ResolutionDisplayType.Card;
			const imageWidth = parseInt(DriveItemUtils.getImageWidth(driveItem), 10);
			const targetWidth = DriveItemUtils.getTargetWidth(currentDisplayType);

			if (driveItem.associatedResolutions && imageWidth >= targetWidth) {
				const availableResolutionsId = driveItem.associatedResolutions[iconId] || 'default';
				const availableResolutions = resolutions[availableResolutionsId];
				if (availableResolutions !== undefined && DriveItemUtils.isSupportedExtension(driveItem, resolutions)) {
					const resolution = DriveItemUtils.getIdealSizeFromResolutions(availableResolutions, currentDisplayType, isBox);
					return this.getImagePathByResolution(driveItem, galleryConfig, resolution);
				}
			}
			return this.getIconOriginalPath(driveItem, galleryConfig);
		}
		return this.getDefaultThumbnail(driveItem);
	}

	static getTheaterImage(driveItem: Api.DriveFolderInfo, galleryConfig: Api.Config, globalResolutions: { [k: string]: Api.ResolutionGroup }): string {
		const resolutions = driveItem.resolutions || globalResolutions;
		const imageWidth = parseInt(DriveItemUtils.getImageWidth(driveItem), 10);
		const targetWidth = DriveItemUtils.getTargetWidth(ResolutionDisplayType.Theater);
		const extension = Utils.getFileExtension(driveItem.originalFile);
		const isSvg = extension === 'svg';
		const isGif = extension === 'gif';

		if (!driveItem.associatedResolutions || imageWidth < Math.max(targetWidth, maxOriginalImageSize) || isSvg || isGif ) {
			return UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
		}

		const availableResolutionsId = driveItem.associatedResolutions[driveItem.id] || 'default';
		const availableResolutions = resolutions[availableResolutionsId];

		const resolution = DriveItemUtils.getIdealSizeFromResolutions(availableResolutions, ResolutionDisplayType.Theater, true);
		return this.getImagePathByResolution(driveItem, galleryConfig, resolution);
	}

	static hasIcon(driveItem: Api.DriveItem): boolean {
		return !!driveItem.icon;
	}

	static getIconId(driveItem: Api.DriveItem): number {
		if (!driveItem.icon) {
			return -1;
		}
		const regexMatches = /^\$media\$([0-9]+)\..*$/.exec( driveItem.icon );
		return regexMatches !== null
			? parseInt(regexMatches[1], 10)
			: -1;
	}

	static isSupportedExtension(driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup}): boolean {
		const resolvedResolution = driveItem.associatedResolutions ? resolutions[driveItem.associatedResolutions[driveItem.id]] : resolutions.default;
		if (resolvedResolution) {
			const supportedExtensions = resolvedResolution.supported;
			const iconExtension = DriveItemUtils.getIconExtension(driveItem);
			return supportedExtensions.includes(iconExtension);
		}
		return false;
	}

	static getIconExtension(driveItem: Api.DriveItem): string {
		if (driveItem.icon) {
			const extensionRegex = /(?:\.([^.]+))?$/;
			const regexMatch = extensionRegex.exec(driveItem.icon);
			return !!regexMatch && !!regexMatch[1] ? regexMatch[1] : '';
		}
		return '';
	}

	static getDownloadedFileName(driveItem: Api.DriveItem): string {
		const extension = Utils.getFileExtension(driveItem.originalFile);
		let fileName;

		if (extension === 'tif') {
			fileName = `${driveItem.id}.jpg`;
		} else {
			fileName = `${driveItem.id}.${extension}`;
		}

		return fileName;
	}

	static getTargetWidth(type: ResolutionDisplayType): number {
		switch (type) {
			case ResolutionDisplayType.Theater:
				return Math.max(window.innerHeight, window.innerWidth);
			case ResolutionDisplayType.RowItem:
				return minimumWidthRow;
			default:
				return minimumWidth;
		}
	}

	static getImageWidth(driveItem: Api.DriveItem): string {
		if (driveItem.resolution) {
			const widthRegex = /(\d+)x/;
			const regexMatch = widthRegex.exec(driveItem.resolution);
			return !!regexMatch && !!regexMatch[1] ? regexMatch[1] : '';
		}

		return '';
	}

	static getIdealSizeFromResolutions(availableResolutions: Api.ResolutionGroup, type: ResolutionDisplayType, isBox: boolean): Api.ResolutionSize {
		const targetWidth = DriveItemUtils.getTargetWidth(type);

		// Try to find an associated box that fits
		let resolvedSize = this.getIdealSizeFromSizes(availableResolutions.sizes, targetWidth, isBox);

		// If no box fit, we ignore the isBox parameter
		resolvedSize = resolvedSize || this.getIdealSizeFromSizes(availableResolutions.sizes, targetWidth);

		// If still no box fit, we use the biggest resolution
		if (!resolvedSize) {
			for (const metaSize of availableResolutions.sizes) {
				if (!resolvedSize || metaSize.width > resolvedSize.width) {
					resolvedSize = metaSize;
				}
			}
		}

		return <Api.ResolutionSize> resolvedSize;
	}

	static getIdealSizeFromSizes(metaSizes: Api.ResolutionSize[], targetWidth: number, isBox?: boolean): Api.ResolutionSize | undefined {
		let resolvedSize: Api.ResolutionSize | undefined;

		for (const metaSize of metaSizes) {
			if ((typeof isBox === 'undefined' || metaSize.box === isBox)
			&& (metaSize.width >= targetWidth && (!resolvedSize || metaSize.width < resolvedSize.width))) {
				resolvedSize = metaSize;
			}
		}
		return resolvedSize;
	}

	static getImagePathByResolution(driveItem: Api.DriveItem, galleryConfig: Api.Config, size: Api.ResolutionSize): string {
		const extension = size.extension || (driveItem.type === Api.DriveItemType.Image ? this.getExtension(driveItem) : 'jpg');
		return UrlUtils.getImageUrl(galleryConfig, driveItem, size, extension);
	}

	static getIconOriginalName(driveItem: Api.DriveItem): string {
		if (!driveItem.icon) {
			return '';
		}

		return this.getAssetOriginalName(driveItem.icon);
	}

	static getAssetOriginalName(ressourceUrl: string): string {
		const regexMatches = /^\$media\$(.+)$/.exec(ressourceUrl );
		return regexMatches !== null
			? regexMatches[1]
			: '';
	}


	static getAssetTypeTranslationKey(driveItemType: Api.DriveItemType, publicationFormat?: Api.PublicationFormat | undefined): string {
		const isProposal = driveItemType === Api.DriveItemType.Publication
			&& publicationFormat
			&& DriveItemUtils.isProposal(publicationFormat);
		if (isProposal) {
			return 'fileFormat.PROPOSAL';
		}

		return `type.asp-type-${driveItemType}`;
	}

	static getIconOriginalPath(driveItem: Api.DriveItem, galleryConfig: Api.Config): string {
		return this.getAssetOriginalPath(galleryConfig, this.getIconOriginalName(driveItem));
	}

	static getAssetOriginalPath(galleryConfig: Api.Config, ressourceName: string) {
		return UrlUtils.getItemOriginalFileUrl(galleryConfig, ressourceName);
	}

	static getDefaultThumbnail(driveItem: Api.DriveItem): string {
		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
				return 'iconset_doc_default';
			case Api.DriveItemType.Publication:
				return 'iconset_publication';
			case Api.DriveItemType.File:
				return 'iconset_file';
			case Api.DriveItemType.Image:
				return 'iconset_image-bold';
			case Api.DriveItemType.Movie:
				return 'iconset_video-bold';
			case Api.DriveItemType.Sound:
				return 'iconset_sound-bold';
			case Api.DriveItemType.Link:
			case Api.DriveItemType.ExternalLink:
				return 'iconset_link';
			case Api.DriveItemType.Gallery:
				return 'iconset_gallery-bold';
			default:
				return 'iconset_page-bold';
		}
	}

	static getDescription(driveItem: Api.DriveItem, { locale }: Api.Template): string {
		const descriptionKeys = Object.keys(driveItem.descriptions);

		if (!!descriptionKeys.length) {
			if (driveItem.descriptions[locale]) {
				return driveItem.descriptions[locale];
			}
			return driveItem.descriptions[descriptionKeys[0]];
		}
		return '';
	}

	static getPublicationCover(path: string, page: number = 1): string {
		return path.replace(/{page}/, page.toString());
	}

	static isStored(itemId: number, storage: number[]): boolean {
		return storage.includes(itemId);
	}

	static getPageByDriveItemId(ressources: Api.DriveItemResource[], driveItemId: number): number {
		for (let i = 0; i < ressources.length; i++) {
			if (driveItemId === ressources[i].id) {
				return ( 1 + Math.floor( i / DRIVE_ITEM_LIST_PAGE_SIZE ) );
			}
		}
		return -1;
	}

	static getShareTitle(sharedItems: Api.DriveItem[]) {
		const firstItem = sharedItems[0];

		if (sharedItems.length > 1) {
			return I18n.t('messages.selection');
		}

		return DriveItemUtils.getDisplayName(firstItem);
	}

	static getShareLink(sharedItems: Api.DriveItem[], galleryConfig: Api.Config, shareOrigin: ShareOrigin) {

		if (sharedItems.length > 1) {
			return UrlUtils.getUrlDriveItemListPage(galleryConfig, DriveItemListOrigin.Assortment, sharedItems.map(driveItem => driveItem.id).join(), true);
		}

		const firstItem = sharedItems[0];
		if (firstItem.type === Api.DriveItemType.Directory) {
			return UrlUtils.getUrlDriveItemListPage(galleryConfig, DriveItemListOrigin.Folder, firstItem.id.toString(), true);
		}

		switch (shareOrigin) {
			case ShareOrigin.Detail:
				return UrlUtils.getUrlDriveItemDetailPage(galleryConfig, firstItem.id, undefined, true);
			case ShareOrigin.Selection:
				return UrlUtils.getUrlDriveItemListPage(galleryConfig, DriveItemListOrigin.Assortment, sharedItems[0].id.toString(), true);
			case ShareOrigin.Theater:
			default:
				return UrlUtils.getUrlTheater(galleryConfig, DriveItemListOrigin.Folder, firstItem.parent.toString(), firstItem.id, true);
		}
	}

	static containsString(driveItem: Api.DriveItem, searchString: string): boolean {
		const fieldsToCheck = [
			'label',
			'name',
			'descriptions',
			'tags',
			'titles'
		];
		const search = searchString.toLowerCase();

		for (let i = 0; i < fieldsToCheck.length; i++) {
			const field = fieldsToCheck[i];
			const value = driveItem[field];

			if (typeof(value) === 'object') {
				const subFields = Object.keys(value);

				for (let j = 0; j < subFields.length; j++) {
					const subField = subFields[j];
					if (value[subField].toString().toLowerCase().includes(search)) {
						return true;
					}
				}
			} else if (typeof(value) !== 'undefined') {
				if (value.toString().toLowerCase().includes(search)) {
					return true;
				}
			}
		}
		return false;
	}

	static isDownloadableItemType(driveItem: Api.DriveItem): boolean {
		const { type } = driveItem;

		switch (type) {
			case Api.DriveItemType.File:
			case Api.DriveItemType.Image:
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Sound:
			case Api.DriveItemType.Component:
			case Api.DriveItemType.Template:
				return true;
			case Api.DriveItemType.Directory:
			case Api.DriveItemType.V2Gateway:
			case Api.DriveItemType.Link:
			case Api.DriveItemType.AppContainer:
			case Api.DriveItemType.Gallery:
			case Api.DriveItemType.ExternalLink:
				return false;
			case Api.DriveItemType.Publication:
				const isDraftProposal = DriveItemUtils.isDraftProposal(driveItem);
				return !isDraftProposal;
		}
	}

	static isReadable(driveItem: Api.DriveItem, downloadManager: DownloadManagerState): boolean {
		const isOnline = UserAgentUtils.isOnline();
		const isPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);

		let isReadable = false;
		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
			case Api.DriveItemType.Image:
				isReadable = true;
				break;
			case Api.DriveItemType.ExternalLink:
				isReadable = isOnline;
				break;
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Sound:
			case Api.DriveItemType.Publication:
			case Api.DriveItemType.Component:
			case Api.DriveItemType.File:
				isReadable = isOnline || isPersistentlyDownloaded;
				break;
			default:
				break;
		}
		return isReadable;
	}

	static isDownloadable(driveItem: Api.DriveItem, downloadManager: DownloadManagerState, sharedMethods: Api.ShareMethod[] | undefined): boolean {
		const isDownloadableType = DriveItemUtils.isDownloadableItemType(driveItem);
		if (!isDownloadableType) {
			return false;
		}

		const isDesktop = UserAgentUtils.isDesktop();
		const isMobileApp = UserAgentUtils.isMobileApp;
		const isSafariMobile = UserAgentUtils.isSafari() && (!isDesktop && !isMobileApp);
		const isOnline = UserAgentUtils.isOnline();
		const hasDownloadRights = DriveItemUtils.hasDownloadRights(sharedMethods);
		const isPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);

		let isDownloadable = false;
		switch (driveItem.type) {
			case Api.DriveItemType.File:
				isDownloadable = isOnline && hasDownloadRights && (!isMobileApp || !isPersistentlyDownloaded);
				break;
			case Api.DriveItemType.Image:
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Sound:
			case Api.DriveItemType.Publication:
				// Safari Mobile opens assets instead of downloading them
				isDownloadable = isOnline && hasDownloadRights && (!isMobileApp || !isPersistentlyDownloaded) && !isSafariMobile;
				break;
			case Api.DriveItemType.Component:
				isDownloadable = isOnline && hasDownloadRights && isMobileApp && !isPersistentlyDownloaded;
				break;
			default:
				break;
		}
		return isDownloadable;
	}

	static isUpdatable(driveItem: Api.DriveItem, downloadManager: DownloadManagerState, sharedMethods: Api.ShareMethod[] | undefined): boolean {
		const isOnline = UserAgentUtils.isOnline();
		const hasDownloadRights = DriveItemUtils.hasDownloadRights(sharedMethods);
		const isPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);
		let downloadedVersion = -1;
		if (isPersistentlyDownloaded) {
			downloadedVersion = downloadManager.items[driveItem.id].version;
		}
		const isUpdatable = isOnline && hasDownloadRights && isPersistentlyDownloaded && driveItem.version > downloadedVersion;

		return isUpdatable;
	}

	static isDeletable(driveItem: Api.DriveItem, downloadManager: DownloadManagerState): boolean {
		return DownloadManagerUtils.isRequestedItem(downloadManager, driveItem.id);
	}

	static hasDownloadRights(sharedMethods: Api.ShareMethod[] | undefined): boolean {
		return sharedMethods !== undefined && sharedMethods.includes(Api.ShareMethod.Download);
	}

	static isShareable(sharedMethods: Api.ShareMethod[] | undefined): boolean {
		return sharedMethods !== undefined && sharedMethods.includes(Api.ShareMethod.Share);
	}

	static isProposal(format: Api.PublicationFormat): boolean {
		return [
			Api.PublicationFormat.PDF_EDITABLE,
			Api.PublicationFormat.EPUB_EDITABLE,
			Api.PublicationFormat.EPUB_MASTER,
		].includes(format as Api.PublicationFormat);
	}

	static isDraftProposal(driveItem: Api.DriveItem): boolean {
		const { format, proposalStats } = driveItem;
		const isProposal = DriveItemUtils.isProposal(format as Api.PublicationFormat);
		const isDraftProposal = isProposal && !!proposalStats && proposalStats.status === Api.ProposalStatus.DRAFT;

		return isDraftProposal;
	}

	static isForbiddenForUpdateProposal(driveItem: Api.DriveItem): boolean {
		const { format, proposalStats } = driveItem;
		const isProposal = DriveItemUtils.isProposal(format as Api.PublicationFormat);
		return isProposal && !!proposalStats && proposalStats.forbiddenUpdate;
	}

	static isEditable(driveItem: Api.DriveItem): boolean {
		const { format, componentType } = driveItem;
		const isPresentation = format === Api.PublicationFormat.EPUB_PRESENTATION;
		const isEditableComponent = componentType === Api.ComponentType.ZIP_EDITABLE;

		return (DriveItemUtils.isDraftProposal(driveItem) && !DriveItemUtils.isForbiddenForUpdateProposal(driveItem))
			|| isPresentation
			|| isEditableComponent;
	}

	static isInSelection(driveItem: DriveItemInfo, selection: number[]): boolean {
		return selection.includes(driveItem.id);
	}

	static isUnread(driveList: DriveListState, readStatus: ReadItemsListState, id: number): boolean {
		const { driveItems } = driveList;
		const readItems = readStatus.items;
		return readItems[id] ? driveItems[id].version > readItems[id].lastVersionRead : true;
	}

	static isValidResourceForProposal({ type, name, componentType }: Api.DriveItem): boolean {
		const resourceTypes = [
			Api.DriveItemType.Image,
			Api.DriveItemType.Movie,
			Api.DriveItemType.Publication,
		];

		const fileExtensions = ['.pdf', '.epub', '.ppt', '.pptx', '.doc', '.docx', '.otp', '.odt'];
		const fileExtension = name ? `.${Utils.getFileExtension(name)}` : '';

		// we have to set isValidFile to true to fetch content-block before we know 'name' of resource
		const isValidFile = type === Api.DriveItemType.File && (!name
			? true
			: fileExtensions.includes(fileExtension));

		// we have to set isValidComponent to true to fetch content-block before we know 'componentType' of resource
		const isValidComponent = type === Api.DriveItemType.Component && (!componentType
			? true
			: ![Api.ComponentType.ZIP, Api.ComponentType.ZIP_EDITABLE].includes(componentType));

		return resourceTypes.includes(type) || isValidFile || isValidComponent;
	}

	static convertDisplayTypeFormat(aspDisplayType: Api.AspFolderDisplayType): ListDisplayType {
		switch (aspDisplayType) {
			case Api.AspFolderDisplayType.List:
				return ListDisplayType.Row;
			case Api.AspFolderDisplayType.Masonry:
				return ListDisplayType.Masonry;
			case Api.AspFolderDisplayType.Coverflow:
				return ListDisplayType.Coverflow;
			case Api.AspFolderDisplayType.Grid:
			default:
				return ListDisplayType.Grid;
		}
	}

	static isPrivateDirDescendant(folderId: number | undefined, privateDirId: number | undefined, driveItems: { [k: number]: DriveItemInfo }): boolean {
		if (!folderId || !privateDirId || !driveItems[folderId]) {
			return false;
		}

		if (folderId === privateDirId) {
			return true;
		}

		const { parent } = driveItems[folderId];
		return DriveItemUtils.isPrivateDirDescendant(parent, privateDirId, driveItems);
	}

	static getDriveItemChildren(driveItemId: number): number[] {
		const { driveList: {driveItems} } = store.getState();
		const driveItem = driveItems[driveItemId];

		let children = [driveItemId];
		if (driveItem.resources) {
			children = children.concat(...driveItem.resources.map(resource => DriveItemUtils.getDriveItemChildren(resource.id)));
		}

		return children;
	}

	static getDisplayType = (driveList: DriveListState, routerParamsHistory: RouterParamsState[], folderId?: number | string): ListDisplayType => {
		const {
			foldersDisplayType,
			displayType: storeDisplayType,
		} = driveList;

		// handle displayType of New, Downloads, Selection and Search folders
		const folderIdOrOrigin = folderId || RouteUtils.getCurrentOrigin(routerParamsHistory);

		// high priority: displayMode which is set inside folder via displayType dropdown
		if (foldersDisplayType[folderIdOrOrigin]) {
			return foldersDisplayType[folderIdOrOrigin];
		}

		// middle priority: displayMode which is set in Parameters page (except Default)
		if (storeDisplayType !== ListDisplayType.Default) {
			return storeDisplayType;
		}

		// low priority: displayMode which is set in backoffice
		if (driveList.driveItems[folderIdOrOrigin]) {
			const { displayMode: aspDisplayType } = driveList.driveItems[folderIdOrOrigin];
			if (aspDisplayType) {
				return DriveItemUtils.convertDisplayTypeFormat(aspDisplayType);
			}
		}

		// fallback
		return ListDisplayType.Grid;
	}
}

export {
	ResolutionDisplayType,
	ActionButton,
	ActionButtonStyle,
	ThumbnailDescriptor
};
export default DriveItemUtils;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/driveItem.ts